<script setup>
import { computed, ref } from 'vue'
import { usePlaybackStore } from '@state/stores/playback'
import SharedCarousel from '@components/carousel/shared-carousel'
import SharedCarouselSlide from '@components/carousel/shared-carousel-slide'
import ChannelDetailTile from '@components/channel/channel-detail-tile'
import { useMessages } from '@composables/use-messages'
import { useFavoritesStore } from '@state/stores/favorites'

const props = defineProps({
    channels: {
        type: Array,
        default: () => [],
    },
    playlists: {
        type: [Object, Array],
        default: () => ({}),
    },
    isFavorites: {
        type: Boolean,
        default: false,
    },
    displayMinimalVersion: {
        type: Boolean,
        default: false,
    },
})

const favoritesStore = useFavoritesStore()
const playbackStore = usePlaybackStore()
const getMessageText = useMessages()
const carousel = ref(null)
const activeSlide = ref(0)
const carouselState = ref({
    currentPage: 0,
    pageCount: 0,
    isBeginning: true,
    isEnd: true,
})
const tabs = [
    {
        to: { name: 'channel-index' },
        label: 'All Channels',
    },
    {
        to: { name: 'channel-index/favorites' },
        label: 'Favorites',
    },
]

const favorites = computed(() => favoritesStore.channelIds)
const computedChannels = computed(() =>
    props.isFavorites
        ? props.channels.filter(channel => favorites.value.includes(channel.id))
        : props.channels
)
const resource = computed(() => playbackStore.resource)
const mix = computed(() => playbackStore.mix)
const status = computed(() => playbackStore.status)
const carouselGrid = computed(() =>
    props.displayMinimalVersion
        ? [
            [0, 1],
            [768, 2],
            [1081, 4],
        ]
        : [
            [0, 1],
            [768, 2],
            [1081, 3],
            [1921, 4],
        ]
)

function handleCarouselState(state) {
    activeSlide.value = state.currentPage
    carouselState.value = state
}

function nextSlide() {
    carousel.value?.next()
}

function prevSlide() {
    carousel.value?.prev()
}
</script>

<template>
    <div class="container">
        <!-- Channel Detail Block -->
        <div :class="['row', { minimal: displayMinimalVersion }]">
            <BaseTabPanel
                v-if="!displayMinimalVersion"
                class="tab-navigation"
                :tabs="tabs"
                address-navigation
                is-underlined
                is-unpadded
            >
                <div
                    v-if="carouselState.pageCount > 1"
                    class="carousel-navigation"
                >
                    <span
                        :class="['carousel-nav-icon', { faded: carouselState.isBeginning }]"
                        @click.prevent="prevSlide()"
                    >
                        <BaseIcon
                            class="icon icon-arrow-left"
                            name="arrow-left"
                        />
                    </span>
                    <span
                        :class="[
                            'carousel-nav-icon',
                            { faded: carouselState.isEnd },
                        ]"
                        @click.prevent="nextSlide()"
                    >
                        <BaseIcon
                            class="icon icon-arrow-right"
                            name="arrow-right"
                        />
                    </span>
                </div>
            </BaseTabPanel>
            <SharedCarousel
                ref="carousel"
                :per-page-custom="carouselGrid"
                :pagination-enabled="false"
                :resistance-coef="50"
                :scroll-per-page="false"
                :min-swipe-distance="100"
                :space-padding="12"
                :class="['carousel', { minimal: displayMinimalVersion }]"
                @state-change="handleCarouselState"
            >
                <SharedCarouselSlide
                    v-for="(channel, index) in computedChannels"
                    :key="channel.id"
                    class="slide"
                >
                    <ChannelDetailTile
                        :channel="channel"
                        :index="index"
                        :playlist="playlists[channel.id]"
                        :display-minimal-version="displayMinimalVersion"
                        :active-slide="activeSlide"
                        :class="[
                            'item',
                            {
                                faded:
                                    status === 'playing' &&
                                    resource &&
                                    resource.id !== channel.id &&
                                    (!mix || resource.id !== mix.id),
                            },
                            { minimal: displayMinimalVersion },
                        ]"
                    />
                </SharedCarouselSlide>
            </SharedCarousel>

            <BaseSection
                v-if="computedChannels.length === 0 && isFavorites && !displayMinimalVersion"
                padded
            >
                <div class="message">
                    <h4 class="message-title">
                        {{ getMessageText('NO_FAVORITE_CHANNEL') }}
                    </h4>
                    <p class="message-paragraph">
                        {{ getMessageText('DISCOVER_NEW_CHANNELS') }}
                    </p>
                    <BaseLink :to="{ name: 'channel-index' }">
                        <BaseButton icon="signal"> Discover New Channels </BaseButton>
                    </BaseLink>
                </div>
            </BaseSection>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@use 'sass:math';
@use '@design' as *;

.container {
    @include container;

    overflow: hidden;

    @include tablet {
        overflow: visible;
    }
}
.row {
    display: flex;
    flex-direction: column;
    &.minimal {
        margin-right: -24px;
        margin-left: -24px;
    }
}

.item {
    padding: gutter() 0;

    @include transition(opacity, slow);
    &.minimal {
        padding: 0;
    }
}

.slide {
    padding: 0 math.div(gutter(), 2);
}

.tab-navigation {
    display: none;

    @include tablet {
        display: block;
    }
}

.carousel-navigation {
    padding: 0 gutter();
    text-align: right;
}

.carousel-nav-icon {
    display: inline-block;
    width: 36px;
    height: 36px;
    text-align: center;
    cursor: pointer;
    background: $c-surface-raised;
    border-radius: $radius-8;

    @include transition(opacity);
    &:first-of-type {
        margin-right: 8px;
    }
    &.faded {
        opacity: 0.2;
    }
    &:hover:not(.faded) {
        background: $c-surface-overlay;
    }
}

.icon-arrow-left,
.icon-arrow-right {
    width: 16px;
    height: 36px;
    color: $c-text-tertiary;
    transform: scale(1.3, 0.8);
}

.carousel {
    :deep(> div) {
        overflow: visible;
    }
}

.message {
    max-width: 40rem;
    margin: $s-h * 2 auto $s-xl;
    text-align: center;

    &-paragraph {
        @extend %contentSubtitleL;

        max-width: 25rem;
        margin-right: auto;
        margin-bottom: $s-xxl;
        margin-left: auto;
        color: $c-text-faded;
    }

    &-title {
        margin-bottom: $s-l;
    }
}

@media (min-width: $desktop) {
    .item {
        &.faded {
            opacity: 0.2;
        }
        &:hover {
            opacity: 1;

            @include transition(opacity, base);
        }
    }
}
</style>
